import React, { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from 'react-query'
import { AlertTriangle, BadgeCheck, CircleDot, Laptop, Loader2, ShieldAlert, ShieldCheck, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Skeleton } from '@/components/ui/skeleton'
import { oauthService } from '@/services/oauthService'
import { useAuthStore } from '@/store/authStore'
import { cn } from '@/lib/utils'

// "Connect by signing in" for AI tools (OAuth). The app opened /api/oauth/authorize, which sent
// the browser here with ?request=<handle>. The user checks who is asking, where the answer
// goes and which permissions to give, then approves or denies; we send the browser back to
// the app either way. See backend/src/oauth/authorize.js.

const Shell = ({ children }) => (
  <div className="min-h-screen flex items-start sm:items-center justify-center bg-background px-4 py-10">
    <div className="w-full max-w-lg space-y-4">
      <div className="flex items-center justify-center gap-2 text-sm font-medium text-muted-foreground">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-primary/20 bg-primary/10">
          <CircleDot className="h-4 w-4 text-primary" />
        </span>
        Buzzinga
      </div>
      {children}
    </div>
  </div>
)

const Message = ({ icon: Icon = XCircle, tone = 'error', title, children }) => (
  <Shell>
    <Card>
      <CardContent className="pt-6 space-y-3 text-center">
        <Icon className={cn('mx-auto h-8 w-8', tone === 'error' ? 'text-destructive' : 'text-primary')} />
        <h1 className="text-lg font-semibold">{title}</h1>
        <div className="text-sm text-muted-foreground">{children}</div>
      </CardContent>
    </Card>
  </Shell>
)

const READ_ONLY = (s) => s.startsWith('read:')

const OAuthConsent = () => {
  const [params] = useSearchParams()
  const handle = params.get('request')
  const serverError = params.get('error')
  const { user, logout } = useAuthStore()
  const [selected, setSelected] = useState(null)
  const [busy, setBusy] = useState(null) // 'approve' | 'deny'
  const [sentTo, setSentTo] = useState(null)
  const [actionError, setActionError] = useState(null)
  const framed = typeof window !== 'undefined' && window.top !== window.self

  const { data, error, isLoading } = useQuery(
    ['oauth-request', handle],
    () => oauthService.getRequest(handle),
    { enabled: !!handle && !serverError && !framed && !!user, retry: false, refetchOnWindowFocus: false }
  )
  const request = data?.data

  useEffect(() => {
    if (request && selected === null) setSelected(new Set(request.offered_scopes))
  }, [request, selected])

  const scopeInfo = useMemo(() => Object.fromEntries((request?.scopes || []).map(s => [s.id, s])), [request])

  if (framed) {
    return <Message title="This page can't be shown inside another site">Open Buzzinga in its own browser tab to connect an app.</Message>
  }
  if (serverError) {
    return (
      <Message title="Couldn't connect the app">
        <p>{params.get('error_description') || 'The request was not valid.'}</p>
        <p className="mt-2">Start connecting again from your AI tool.</p>
      </Message>
    )
  }
  if (!handle) {
    return <Message title="Nothing to approve">Start connecting from your AI tool; it opens this page for you.</Message>
  }
  if (sentTo) {
    return (
      <Message icon={ShieldCheck} tone="ok" title={sentTo.denied ? 'Access denied' : 'Connected'}>
        {sentTo.denied
          ? <p>We told {sentTo.app} that you said no.</p>
          : <p>Taking you back to {sentTo.app}. If nothing happens, return to it yourself; you can close this tab.</p>}
      </Message>
    )
  }
  if (isLoading || (!request && !error)) {
    return (
      <Shell>
        <Card><CardContent className="pt-6 space-y-3"><Skeleton className="h-6 w-2/3" /><Skeleton className="h-4 w-full" /><Skeleton className="h-24 w-full" /></CardContent></Card>
      </Shell>
    )
  }
  if (error) {
    return <Message title="Couldn't load this request">{error.response?.data?.error || 'Something went wrong. Start connecting again from your app.'}</Message>
  }

  const { client, redirect, account } = request
  const offered = request.offered_scopes
  const chosen = selected || new Set()

  const toggle = (scope) => setSelected(prev => {
    const next = new Set(prev)
    if (next.has(scope)) next.delete(scope)
    else next.add(scope)
    return next
  })

  const answer = async (kind) => {
    setBusy(kind)
    setActionError(null)
    try {
      const res = kind === 'approve'
        ? await oauthService.approve(handle, offered.filter(s => chosen.has(s)))
        : await oauthService.deny(handle)
      setSentTo({ app: client.name, denied: kind === 'deny' })
      window.location.assign(res.data.redirect_to)
    } catch (err) {
      setActionError(err.response?.data?.error || 'Something went wrong. Try again.')
      setBusy(null)
    }
  }

  const switchAccount = () => {
    // Sign out and come straight back here after signing in again
    localStorage.removeItem('auth-storage')
    logout()
  }

  return (
    <Shell>
      <Card>
        <CardContent className="pt-6 space-y-5">
          <div className="space-y-1.5">
            <h1 className="text-xl font-semibold leading-snug">
              <span className="text-primary">{client.name}</span> wants to use Buzzinga as you
            </h1>
            {client.verified_host ? (
              <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <BadgeCheck className="h-4 w-4 text-green-600" /> Identified by <span className="font-medium text-foreground">{client.host}</span>
              </p>
            ) : (
              <p className="flex items-center gap-1.5 text-sm text-amber-700 dark:text-amber-400">
                <ShieldAlert className="h-4 w-4" /> Unverified app: it registered itself, so its name isn't checked
              </p>
            )}
          </div>

          <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm space-y-1">
            <div className="flex justify-between gap-3"><span className="text-muted-foreground">Signed in as</span><span className="font-medium truncate">{account.name} · {account.email}</span></div>
            <div className="flex justify-between gap-3"><span className="text-muted-foreground">Sends you back to</span><span className="font-mono text-xs truncate">{redirect.host}</span></div>
            <div className="flex justify-between gap-3"><span className="text-muted-foreground">Connection lasts</span><span>{request.connection_days} days</span></div>
          </div>

          {request.local_app && (
            <div className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
              <Laptop className="h-4 w-4 mt-0.5 shrink-0" />
              <span>This app runs on your computer (Claude Code, an editor or a terminal). Only approve if <strong>you</strong> just started connecting it.</span>
            </div>
          )}

          {request.denied_reason ? (
            <div className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{request.denied_reason}</span>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-medium">It will be able to</h2>
                <div className="flex gap-2 text-xs">
                  <button type="button" className="text-primary hover:underline" onClick={() => setSelected(new Set(offered))}>All</button>
                  <button type="button" className="text-primary hover:underline" onClick={() => setSelected(new Set(offered.filter(READ_ONLY)))}>Read only</button>
                </div>
              </div>
              <ul className="divide-y rounded-md border">
                {offered.map(scope => (
                  <li key={scope}>
                    <label className="flex cursor-pointer items-start gap-3 px-3 py-2">
                      <Checkbox checked={chosen.has(scope)} onCheckedChange={() => toggle(scope)} className="mt-0.5" aria-label={scopeInfo[scope]?.label || scope} />
                      <span className="text-sm">
                        <span className="font-medium">{scopeInfo[scope]?.label || scope}</span>
                        <span className="block text-xs text-muted-foreground">{scopeInfo[scope]?.description}</span>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground">
                It only ever sees what you can see, and only in Project Central. Its changes show as "via {client.name}".
                Disconnect it any time under Profile → API tokens &amp; AI tools.
              </p>
            </div>
          )}

          {actionError && <p className="text-sm text-destructive" role="alert">{actionError}</p>}

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <Button variant="outline" onClick={() => answer('deny')} disabled={!!busy}>
              {busy === 'deny' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Deny
            </Button>
            {!request.denied_reason && (
              <Button onClick={() => answer('approve')} disabled={!!busy || chosen.size === 0}>
                {busy === 'approve' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Approve
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
      <p className="text-center text-xs text-muted-foreground">
        Not {account.name}? <button type="button" className="text-primary hover:underline" onClick={switchAccount}>Use another account</button>
      </p>
    </Shell>
  )
}

export default OAuthConsent
